import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";

const EMPTY_Q = {
  order: 1, difficulty: 1, correct_key: "a", status: "draft",
  prompt: { en: "", ar: "" }, explanation: { en: "", ar: "" },
  choices: [
    { key: "a", text: { en: "", ar: "" } },
    { key: "b", text: { en: "", ar: "" } },
    { key: "c", text: { en: "", ar: "" } },
    { key: "d", text: { en: "", ar: "" } },
  ],
};

export function AdminQuestions({ course }) {
  const { t, lang } = useI18n();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setRows(null);
    try { setRows(await api.adminListQuestions(course.course_id)); }
    catch (e) { toast.error(e.message); setRows([]); }
  };
  useEffect(() => { load(); }, [course.course_id]);

  const openCreate = () => {
    const nextOrder = (rows && rows.length ? Math.max(...rows.map((r) => r.order)) + 1 : 1);
    setEditing({ ...EMPTY_Q, order: nextOrder, course_id: course.course_id });
  };

  const openEdit = (q) => setEditing({
    ...q,
    choices: q.choices.map((c) => ({ ...c, text: { ...c.text } })),
    prompt: { ...q.prompt }, explanation: { ...q.explanation },
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.question_id) {
        const { question_id, course_id, created_at, updated_at, ...body } = editing;
        await api.adminPatchQuestion(question_id, body);
      } else {
        await api.adminCreateQuestion(editing);
      }
      toast.success(t("admin.updated")); setEditing(null); await load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (q) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try { await api.adminDeleteQuestion(q.question_id); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-black/60">{rows?.length || 0} {t("exam.questions")}</div>
        <Button onClick={openCreate} className="rounded-full bg-[#4361EE] text-white font-bold px-4"
                data-testid="admin-new-question-btn">
          <Plus className="h-4 w-4 me-1" /> {t("admin.newQuestion")}
        </Button>
      </div>

      {rows === null ? (
        <div className="text-black/50">{t("superadmin.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-black/10 p-8 text-center text-black/50">{t("superadmin.empty")}</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((q) => (
            <li key={q.question_id}
                className="rounded-2xl bg-white border border-black/5 p-4 flex items-center gap-3"
                data-testid={`admin-question-${q.question_id}`}>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#F72585]/10 text-[#F72585] font-black text-sm">
                {q.order}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{q.prompt?.[lang] || q.prompt?.en}</div>
                <div className="text-xs text-black/50">correct: <b>{q.correct_key}</b></div>
              </div>
              <Badge className={`text-[10px] ${q.status === "published" ? "bg-[#4CC9F0]/20 text-[#0A5566]" : "bg-[#FFB703]/20 text-[#7A4B00]"}`}>
                {q.status === "published" ? t("admin.published") : t("admin.draft")}
              </Badge>
              <Button variant="ghost" onClick={() => openEdit(q)} className="rounded-full"
                      data-testid={`admin-edit-question-${q.question_id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" onClick={() => del(q)} className="rounded-full text-[#D90429]"
                      data-testid={`admin-delete-question-${q.question_id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4"
             data-testid="admin-question-editor">
          <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-lg">{editing.question_id ? t("admin.updated") : t("admin.newQuestion")}</h3>
              <button onClick={() => setEditing(null)} className="rounded-full p-2 hover:bg-black/5"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">{t("admin.order")}</Label>
                <Input type="number" value={editing.order}
                       onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || "1", 10) })}
                       data-testid="admin-q-order" /></div>
              <div><Label className="text-xs font-bold">{t("admin.difficulty")}</Label>
                <Input type="number" min={1} max={5} value={editing.difficulty}
                       onChange={(e) => setEditing({ ...editing, difficulty: parseInt(e.target.value || "1", 10) })}
                       data-testid="admin-q-difficulty" /></div>
            </div>
            <div>
              <Label className="text-xs font-bold">{t("admin.promptEn")}</Label>
              <Textarea rows={2} value={editing.prompt.en}
                        onChange={(e) => setEditing({ ...editing, prompt: { ...editing.prompt, en: e.target.value } })}
                        data-testid="admin-q-prompt-en" />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("admin.promptAr")}</Label>
              <Textarea dir="rtl" rows={2} value={editing.prompt.ar}
                        onChange={(e) => setEditing({ ...editing, prompt: { ...editing.prompt, ar: e.target.value } })}
                        data-testid="admin-q-prompt-ar" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold">{t("admin.correctChoice")}</Label>
              {editing.choices.map((c, i) => (
                <div key={c.key} className="grid grid-cols-[40px_1fr_1fr_40px] gap-2 items-center"
                     data-testid={`admin-q-choice-${c.key}`}>
                  <div className="font-black text-lg text-black/60">{c.key.toUpperCase()}</div>
                  <Input value={c.text.en}
                         placeholder="EN"
                         onChange={(e) => {
                           const next = [...editing.choices];
                           next[i] = { ...next[i], text: { ...next[i].text, en: e.target.value } };
                           setEditing({ ...editing, choices: next });
                         }} />
                  <Input dir="rtl" value={c.text.ar}
                         placeholder="AR"
                         onChange={(e) => {
                           const next = [...editing.choices];
                           next[i] = { ...next[i], text: { ...next[i].text, ar: e.target.value } };
                           setEditing({ ...editing, choices: next });
                         }} />
                  <input type="radio" name="correct-key"
                         checked={editing.correct_key === c.key}
                         onChange={() => setEditing({ ...editing, correct_key: c.key })}
                         data-testid={`admin-q-correct-${c.key}`} />
                </div>
              ))}
            </div>

            <div>
              <Label className="text-xs font-bold">{t("admin.explanationEn")}</Label>
              <Textarea rows={2} value={editing.explanation.en}
                        onChange={(e) => setEditing({ ...editing, explanation: { ...editing.explanation, en: e.target.value } })}
                        data-testid="admin-q-explanation-en" />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("admin.explanationAr")}</Label>
              <Textarea dir="rtl" rows={2} value={editing.explanation.ar}
                        onChange={(e) => setEditing({ ...editing, explanation: { ...editing.explanation, ar: e.target.value } })}
                        data-testid="admin-q-explanation-ar" />
            </div>

            <label className="flex items-center gap-2 font-semibold text-sm">
              <input type="checkbox" checked={editing.status === "published"}
                     onChange={(e) => setEditing({ ...editing, status: e.target.checked ? "published" : "draft" })}
                     data-testid="admin-q-published" />
              {t("admin.publish")}
            </label>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-full">{t("admin.cancel")}</Button>
              <Button onClick={save} disabled={saving || !editing.prompt.en}
                      className="rounded-full bg-[#4361EE] text-white font-bold px-6 shadow-[0_3px_0_#2A3EB1]"
                      data-testid="admin-q-save">
                {saving ? "…" : t("admin.save")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
